import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Card,
  CardContent,
} from '@mui/material'
import {
  Receipt as ReceiptIcon,
  PictureAsPdf as PdfIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'

interface Invoice {
  id: string
  invoiceNumber: string
  billingPeriodStart: string
  billingPeriodEnd: string
  baseAmount: number
  userAmount: number
  aiCreditAmount: number
  subtotal: number
  taxRate: number
  taxAmount: number
  totalAmount: number
  userCount: number
  activeUserCount: number
  aiCreditsIncluded: number
  aiCreditsUsed: number
  aiCreditsExtra: number
  status: 'DRAFT' | 'PENDING' | 'SENT' | 'PAID' | 'OVERDUE'
  dueDate: string
  paidAt: string | null
  lineItems: LineItem[]
  createdAt: string
}

interface LineItem {
  description: string
  quantity: number
  unitPrice: number
  amount: number
}

const InvoiceHistoryPage: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { token } = useAuth()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)

  const isJapanese = i18n.language === 'ja'

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true)
      // CORPORATE_ADMINは自テナントの請求書のみ取得（バックエンドでフィルタリング）
      const response = await fetch('/api/billing/invoices', {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setInvoices(data.invoices || data)
      } else {
        throw new Error('Failed to fetch invoices')
      }
    } catch (err) {
      setError(isJapanese ? '請求書の読み込みに失敗しました' : 'Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }, [token, isJapanese])

  useEffect(() => {
    fetchInvoices()
  }, [fetchInvoices])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ja-JP', {
      style: 'currency',
      currency: 'JPY',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(isJapanese ? 'ja-JP' : 'en-US')
  }

  const formatPeriod = (start: string, _end: string) => {
    const startDate = new Date(start)
    if (isJapanese) {
      return `${startDate.getFullYear()}年${startDate.getMonth() + 1}月`
    }
    return startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
  }

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' => {
    switch (status) {
      case 'PAID':
        return 'primary'
      case 'SENT':
        return 'secondary'
      case 'PENDING':
        return 'secondary'
      case 'OVERDUE':
        return 'error'
      case 'DRAFT':
        return 'default'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: string) => {
    if (isJapanese) {
      switch (status) {
        case 'DRAFT':
          return '下書き'
        case 'PENDING':
          return '保留中'
        case 'SENT':
          return '送信済み'
        case 'PAID':
          return '支払済み'
        case 'OVERDUE':
          return '期限超過'
        default:
          return status
      }
    }
    switch (status) {
      case 'DRAFT':
        return 'Draft'
      case 'PENDING':
        return 'Pending'
      case 'SENT':
        return 'Sent'
      case 'PAID':
        return 'Paid'
      case 'OVERDUE':
        return 'Overdue'
      default:
        return status
    }
  }

  const handleViewDetail = (invoice: Invoice) => {
    setSelectedInvoice(invoice)
    setDetailDialogOpen(true)
  }

  const handleDownloadPdf = async (invoiceId: string) => {
    try {
      const response = await fetch(`/api/billing/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `invoice-${invoiceId}.pdf`
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      } else {
        setError(isJapanese ? 'PDFのダウンロードに失敗しました' : 'Failed to download PDF')
      }
    } catch (err) {
      setError(isJapanese ? 'PDFのダウンロードに失敗しました' : 'Failed to download PDF')
    }
  }

  // 集計情報（invoicesが配列であることを確認）
  const invoiceList = Array.isArray(invoices) ? invoices : []

  const totalPaid = invoiceList
    .filter((inv) => inv.status === 'PAID')
    .reduce((sum, inv) => sum + inv.totalAmount, 0)

  const totalUnpaid = invoiceList
    .filter((inv) => ['SENT', 'PENDING', 'OVERDUE'].includes(inv.status))
    .reduce((sum, inv) => sum + inv.totalAmount, 0)

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        {isJapanese ? '請求書履歴' : 'Invoice History'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 集計カード */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 3 }}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ReceiptIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                {isJapanese ? '請求書数' : 'Total Invoices'}
              </Typography>
            </Stack>
            <Typography variant="h4" sx={{ mt: 1 }}>
              {invoiceList.length}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {isJapanese ? '支払済み総額' : 'Total Paid'}
            </Typography>
            <Typography variant="h4" color="primary.main" sx={{ mt: 1 }}>
              {formatCurrency(totalPaid)}
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {isJapanese ? '未払い総額' : 'Total Unpaid'}
            </Typography>
            <Typography variant="h4" color="secondary.main" sx={{ mt: 1 }}>
              {formatCurrency(totalUnpaid)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* 請求書一覧 */}
      <Paper sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ReceiptIcon color="primary" />
          <Typography variant="h6">{isJapanese ? '請求書一覧' : 'Invoices'}</Typography>
        </Stack>

        {invoiceList.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{isJapanese ? '請求書番号' : 'Invoice #'}</TableCell>
                  <TableCell>{isJapanese ? '請求期間' : 'Period'}</TableCell>
                  <TableCell align="right">{isJapanese ? '金額（税込）' : 'Total'}</TableCell>
                  <TableCell>{isJapanese ? '支払期限' : 'Due Date'}</TableCell>
                  <TableCell>{isJapanese ? 'ステータス' : 'Status'}</TableCell>
                  <TableCell align="center">{isJapanese ? '操作' : 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceList.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>
                      <Typography fontWeight="medium">{invoice.invoiceNumber}</Typography>
                    </TableCell>
                    <TableCell>
                      {formatPeriod(invoice.billingPeriodStart, invoice.billingPeriodEnd)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography fontWeight="medium">
                        {formatCurrency(invoice.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(invoice.status)}
                        color={getStatusColor(invoice.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title={isJapanese ? '詳細' : 'View Details'}>
                          <IconButton size="small" onClick={() => handleViewDetail(invoice)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isJapanese ? 'PDFダウンロード' : 'Download PDF'}>
                          <IconButton size="small" onClick={() => handleDownloadPdf(invoice.id)}>
                            <PdfIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {isJapanese ? '請求書がありません' : 'No invoices found'}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* 請求書詳細ダイアログ */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isJapanese ? '請求書詳細' : 'Invoice Details'}
          {selectedInvoice && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedInvoice.invoiceNumber}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedInvoice && (
            <Stack spacing={3}>
              {/* 基本情報 */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? '請求期間' : 'Billing Period'}
                  </Typography>
                  <Typography>
                    {formatDate(selectedInvoice.billingPeriodStart)} -{' '}
                    {formatDate(selectedInvoice.billingPeriodEnd)}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? '支払期限' : 'Due Date'}
                  </Typography>
                  <Typography>{formatDate(selectedInvoice.dueDate)}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {isJapanese ? 'ステータス' : 'Status'}
                  </Typography>
                  <Chip
                    label={getStatusLabel(selectedInvoice.status)}
                    color={getStatusColor(selectedInvoice.status)}
                    size="small"
                  />
                </Box>
              </Box>

              <Divider />

              {/* 明細 */}
              <Box>
                <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                  {isJapanese ? '明細' : 'Line Items'}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{isJapanese ? '項目' : 'Description'}</TableCell>
                        <TableCell align="right">{isJapanese ? '数量' : 'Qty'}</TableCell>
                        <TableCell align="right">{isJapanese ? '単価' : 'Unit Price'}</TableCell>
                        <TableCell align="right">{isJapanese ? '金額' : 'Amount'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedInvoice.lineItems?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Divider />

              {/* 合計 */}
              <Box>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{isJapanese ? '基本料金' : 'Base Amount'}</Typography>
                    <Typography>{formatCurrency(selectedInvoice.baseAmount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>
                      {isJapanese
                        ? `ユーザー料金 (${selectedInvoice.activeUserCount}名)`
                        : `User Fees (${selectedInvoice.activeUserCount} users)`}
                    </Typography>
                    <Typography>{formatCurrency(selectedInvoice.userAmount)}</Typography>
                  </Box>
                  {selectedInvoice.aiCreditAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>
                        {isJapanese
                          ? `追加AIクレジット (${selectedInvoice.aiCreditsExtra}回)`
                          : `Extra AI Credits (${selectedInvoice.aiCreditsExtra})`}
                      </Typography>
                      <Typography>{formatCurrency(selectedInvoice.aiCreditAmount)}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>{isJapanese ? '小計' : 'Subtotal'}</Typography>
                    <Typography>{formatCurrency(selectedInvoice.subtotal)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>
                      {isJapanese
                        ? `消費税 (${(selectedInvoice.taxRate * 100).toFixed(0)}%)`
                        : `Tax (${(selectedInvoice.taxRate * 100).toFixed(0)}%)`}
                    </Typography>
                    <Typography>{formatCurrency(selectedInvoice.taxAmount)}</Typography>
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">{isJapanese ? '合計' : 'Total'}</Typography>
                    <Typography variant="h6" color="primary.main">
                      {formatCurrency(selectedInvoice.totalAmount)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* AIクレジット情報 */}
              <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {isJapanese ? 'AIクレジット情報' : 'AI Credit Information'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {isJapanese ? '付与' : 'Included'}
                    </Typography>
                    <Typography>{selectedInvoice.aiCreditsIncluded}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {isJapanese ? '使用' : 'Used'}
                    </Typography>
                    <Typography>{selectedInvoice.aiCreditsUsed}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {isJapanese ? '追加購入' : 'Extra'}
                    </Typography>
                    <Typography>{selectedInvoice.aiCreditsExtra}</Typography>
                  </Box>
                </Box>
              </Box>

              {selectedInvoice.paidAt && (
                <Alert severity="success">
                  {isJapanese
                    ? `${formatDate(selectedInvoice.paidAt)} に支払い済み`
                    : `Paid on ${formatDate(selectedInvoice.paidAt)}`}
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>{t('buttons.close')}</Button>
          {selectedInvoice && (
            <Button
              variant="contained"
              startIcon={<PdfIcon />}
              onClick={() => handleDownloadPdf(selectedInvoice.id)}
            >
              {isJapanese ? 'PDFダウンロード' : 'Download PDF'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default InvoiceHistoryPage
